<template>
  <div v-if="visible" class="team-mode-banner-root">
    <div
      class="team-mode-banner-wrap"
      :class="{ 'team-mode-banner-wrap--mobile-hidden': isMobile && !isScrollVisible }"
    >
      <div class="team-mode-banner" role="region" aria-label="Team mode">
        <RouterLink :to="{ name: 'teams' }" class="team-mode-banner__back" @click="onLeave">
          <VaIcon name="arrow_back" size="18px" />
          <span>Back from team mode</span>
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'
import { useTeamsStore } from '../../stores/teams'

const route = useRoute()
const store = useTeamsStore()
const breakpoints = useBreakpoint()

const visible = computed(() => route.meta?.teamContext === 'inside' || !!route.params?.teamId)

const isMobile = ref(false)
const isScrollVisible = ref(true)
const lastScrollY = ref(0)

const updateIsMobile = () => {
  isMobile.value = breakpoints.smDown
  if (!isMobile.value) {
    isScrollVisible.value = true
  }
}

const onScroll = () => {
  if (!isMobile.value) return

  const currentY = window.scrollY || 0
  const delta = currentY - lastScrollY.value

  if (currentY <= 12) {
    isScrollVisible.value = true
  } else if (delta > 4) {
    isScrollVisible.value = true
  } else if (delta < -4) {
    isScrollVisible.value = false
  }

  lastScrollY.value = currentY
}

onMounted(() => {
  updateIsMobile()
  window.addEventListener('resize', updateIsMobile)
  window.addEventListener('scroll', onScroll, { passive: true })
  lastScrollY.value = window.scrollY || 0
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateIsMobile)
  window.removeEventListener('scroll', onScroll)
})

const onLeave = () => {
  store.exitTeam()
}
</script>

<style lang="scss" scoped>
.team-mode-banner-root {
  @media (max-width: 600px) {
    min-height: 48px;
    margin: 15px 8px 0;
  }
}

.team-mode-banner-wrap {
  @media (max-width: 600px) {
    position: fixed;
    top: 55px;
    left: 8px;
    right: 8px;
    z-index: 110;
    transition:
      transform 0.22s ease,
      opacity 0.22s ease;

    &--mobile-hidden {
      transform: translateY(calc(-100% - 12px));
      opacity: 0;
      pointer-events: none;
    }
  }
}

.team-mode-banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 14px;
  margin: 30px 16px 0;
  border-radius: 12px;
  border: 1px solid rgba(0, 115, 230, 0.22);
  background: linear-gradient(90deg, rgba(0, 115, 230, 0.08), rgba(0, 115, 230, 0.02));

  @media (max-width: 600px) {
    margin: 0;
    border-color: rgba(0, 115, 230, 0.38);
    background: linear-gradient(90deg, rgba(0, 115, 230, 0.22), rgba(0, 115, 230, 0.12));
    box-shadow: 0 2px 10px rgba(0, 115, 230, 0.14);
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #0073e6;
    font-weight: 600;
    font-size: 14px;
    text-decoration: none;
    transition: opacity 0.18s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #425466;
    font-size: 13px;
  }

  &__name {
    font-weight: 700;
    color: #1a1a1a;
  }

  &__role {
    text-transform: capitalize;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(0, 115, 230, 0.1);
    color: #0073e6;
    font-weight: 600;
  }
}
</style>
